import { Mentions } from "antd";
import type { FC } from "react";
import { useRef } from "react";

import { useTranslation } from "src/translations";
import { HelperText } from "../../../../documentation/styles/InputStyle";
import type { AntCustomFormField } from "../../types/types";
import { useLoadComponentConfigQuery } from "./configurationApi";

const { Option } = Mentions;

// "[" is the canonical trigger; "/" is a discoverable alias for it (e.g. Slack/Notion-style commands).
const MENTION_PREFIXES = ["[", "/"];

/**
 * Defines how many rows (height) have the Textarea components by default.
 * This value can be overridden by using the field properties defined in the json file.
 */
export const DEFAULT_FIELD_ROWS = 4;

export interface TextTemplatingProps extends AntCustomFormField<string> {
    name: string;
    label: string;
    readOnly: boolean;
    rows?: number;
}

export const TextTemplating: FC<TextTemplatingProps> = ({ onChange, rows, ...restProps }) => {
    const { t } = useTranslation();

    const { data } = useLoadComponentConfigQuery(`text-templating`);
    const options = data?.items || [];

    // Tracks the field's current text so onSelect (below) can locate what was just inserted.
    const latestValueRef = useRef("");

    const onValueChange = (text: string) => {
        latestValueRef.current = text;
        if (onChange) {
            onChange(text);
        }
    };

    // The "/" trigger is only a convenience alias for "[": normalize its insertion back to
    // "[key]" so the stored template text always uses the same bracket syntax no matter which
    // key opened the picker.
    const onSelect = (option: { value?: string }, prefix: string) => {
        if (prefix === "[" || !option.value) {
            return;
        }
        const inserted = `${prefix}${option.value}`;
        const index = latestValueRef.current.lastIndexOf(inserted);
        if (index === -1) {
            return;
        }
        const corrected =
            latestValueRef.current.slice(0, index) + `[${option.value}` + latestValueRef.current.slice(index + inserted.length);
        latestValueRef.current = corrected;
        if (onChange) {
            onChange(corrected);
        }
    };

    return (
        <>
            <Mentions
                autoSize
                prefix={MENTION_PREFIXES}
                rows={rows || DEFAULT_FIELD_ROWS}
                onChange={onValueChange}
                onSelect={onSelect}
                {...restProps}
            >
                {(options || []).map((option) => (
                    <Option key={option.key} value={`${option.key}]`}>
                        {t(option.value)}
                    </Option>
                ))}
            </Mentions>
            <HelperText type="secondary">{t("textTemplating.formatHint")}</HelperText>
        </>
    );
};
